import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { Link } from 'core/routing';
import { imageSrc } from 'apps/file/utils';
import { conf as mainConf } from 'apps/config.yaml';

import { modalStore } from 'apps/modals/app';
import { ModalExpired } from 'apps/modals/freeTrial/expired';
import { clientStore } from 'apps/client/app';

import Icon from 'components/Icon';
import Image from 'components/Image';
import Tooltip from 'components/Tooltip';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';

import styled from 'styled-components';
import { media } from 'legacyComponents/styled/styleUtils';
import {
  Card,
  TagsWrapper,
  CardTag,
  InfoWrapper,
  Title,
  Description,
  CardFooter,
  CardFooterList,
  CardFooterItem,
  CardFooterButton,
  TypeTag,
} from 'legacyComponents/cards/styles';
import { Div } from 'legacyComponents/styled/lib';

import { SharedBy } from 'apps/shared';

// FIXME This can be a functional component
@withNamespaces()
@observer
export class ProjectCard extends React.Component {
  showTrialModal = e => {
    e.preventDefault();
    const project = this.props.cardObject;
    const { Subscribe } =
      project.manager.manager.manager || project.manager.manager;
    modalStore.SetModalComponent(ModalExpired, {
      spaceType: project.space.spaceType,
      clickPurchaseButton: () => Subscribe({ spaceSlug: project.spacePath }),
    });
  };

  render() {
    const { cardObject, cardNo, cardColumns, t } = this.props;

    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='project' />;
    }

    const {
      image,
      descriptionSnippet,
      contributionCount,
      slug,
      spacePath,
      tags,
      space,
      content,
      name,
    } = cardObject;

    const { routerStore } = clientStore;
    const { pageViewsFormatted, followersCount } = content;
    const spaceType = spacePath[0] === '+' ? 'initiative' : 'profile';
    const route = `${spaceType}.project`;

    const tagsList = tags.map((tag, index) => (
      <Link
        name='channel'
        params={{ channel: tag.name }}
        key={tag?.id ? `${tag.id}_${index}` : index}
        content={{ main: true }}
      >
        <CardTag vertical>#{tag.name}</CardTag>
      </Link>
    ));

    const { trialPeriodEnd } =
      this.props.cardObject && this.props.cardObject.space;

    const routeName = routerStore.route.name;
    const showPublicPrivateTag =
      routeName === 'workspace.spaceProjects' ||
      (clientStore.signedInProfile &&
        (routeName === 'home' || routeName === 'workspace'));

    return (
      <Card
        noMargin={noMargin}
        hasTags
        data-intercom-target={cardNo === 0 ? 'organisation-projects-list' : ''}
      >
        <Div position='relative'>
          <Link
            name={route}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
          >
            <Tooltip id='tooltipBanner' />
            {showPublicPrivateTag && cardObject.private && (
              <TypeTag>
                <Icon name='locked' />
                <Trans>Private</Trans>
              </TypeTag>
            )}
            {showPublicPrivateTag && !cardObject.private && (
              <TypeTag isPublic>
                <Icon name='website' />
                <Trans>Public</Trans>
              </TypeTag>
            )}
            <ImageStyled
              src={imageSrc(
                image,
                cardObject.imageFallbackPath,
                mainConf.imgSizes.project.card,
              )}
              alt={name}
            />
          </Link>
          <TagsWrapper withPrivate={cardObject.private && trialPeriodEnd}>
            {tagsList}
          </TagsWrapper>
        </Div>
        <InfoWrapper>
          <Link
            name={route}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
          >
            <Title>{name}</Title>
          </Link>
          <Description>{descriptionSnippet}</Description>
          <SharedBy
            label='Created by:'
            space={space}
            underlined={false}
            variant='primary'
          />
        </InfoWrapper>
        <CardFooter>
          <Tooltip />
          <CardFooterList>
            {!cardObject.private && (
              <CardFooterItem>
                <Icon name='view' data-tip={t('Views')} />
                {pageViewsFormatted}
              </CardFooterItem>
            )}
            <CardFooterItem>
              <Icon name='user' data-tip={t('Followers')} />
              {followersCount}
            </CardFooterItem>
            <CardFooterItem>
              <Icon name='contribution' data-tip={t('Contributions')} />
              {contributionCount}
            </CardFooterItem>
          </CardFooterList>
          <Link
            name={route}
            params={{ [spaceType]: spacePath, slug }}
            content={content}
          >
            <CardFooterButton icon='arrow_right' appearance='plain' />
          </Link>
        </CardFooter>
      </Card>
    );
  }
}

// FIXME Styles should be in another file
const ImageStyled = styled(Image)`
  width: 100%;
  height: 284px;
  transition: opacity 100ms ease-in-out;
  border-bottom: 1px solid ${props => props.theme.color.wfGrey200};
  object-fit: cover;
  &:hover {
    cursor: pointer;
  }
  ${media.l` height: 230px; `};
  ${media.m` height: 250px; `};
  ${media.s` height: 200px; `};
  ${media.xs` height: auto; `};
`;

ProjectCard.propTypes = {
  cardObject: PropTypes.object,
  cardColumns: PropTypes.number,
  cardNo: PropTypes.number,
  t: PropTypes.func,
};
